import React, { useCallback, useMemo } from 'react';
import {
  AddButton,
  ButtonGroup,
  ErrorHandler,
  FilteredTable,
  Icon,
  Link,
  MultiSelectTableFilter,
  PrimaryButton,
  Text,
  TextSearchTableFilter,
  TimeWithTimeZone,
} from '@common/components';
import {
  useQuery,
  useNavigate,
  useLocation,
  usePaginationHelper,
} from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import {
  Membership,
  WorksMembershipsQuery,
  WorksMembershipsResponse,
} from 'works/legacy/graphql/works-memberships';
import FiltersStorage from 'works/legacy/lib/filters';
import {
  WorksMembershipRolesQuery,
  WorksMembershipRolesResponse,
} from 'works/legacy/graphql/works-membership-roles';
import {
  BuilderConfig,
  filterableColumnBuilder,
} from 'works/legacy/lib/table-filters-helper';
import { Dropdown, TableColumnType } from 'antd';
import { ORGANIZATION_ADMIN_MENU } from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu';
import { SUPPLIER_ADMIN_MENU } from 'works/legacy/pages/supplier/admin-settings/supplier-admin-vertical-nav-menu';
import { buildDialogUrl } from '@common/services';

const LOCALSTORAGE_FILTER_KEY = 'MembershipList';

const ACTIVE_FILTER_OPTIONS = [
  { text: 'Active', value: 'true' },
  { text: 'Inactive', value: 'false' },
];

function buildViewDrawerRoute(memberId: string) {
  return buildDialogUrl('user-view-drawer', {
    props: { userId: memberId },
  });
}

function renderName(
  _isOrganization: boolean, // eslint-disable-line @typescript-eslint/no-unused-vars
  value: string,
  member: { id: string; organizationUnitsCount: number; active: boolean },
  _index: number // eslint-disable-line @typescript-eslint/no-unused-vars
) {
  return (
    <Link
      className="members-list-row-link"
      to={buildViewDrawerRoute(member.id)}
    >
      {value}
    </Link>
  );
}

function getColumns(
  builderConfig: BuilderConfig<Membership>,
  isOrganization: boolean
): TableColumnType<Membership>[] {
  const column = filterableColumnBuilder(builderConfig);

  return [
    column({
      key: 'firstName',
      title: 'First Name',
      priority: 1,
      filterDropdown: TextSearchTableFilter,
      width: 120,
      className: 'sentry-mask',
      render: (...args) => renderName(isOrganization, ...args),
    }),
    column({
      key: 'lastName',
      title: 'Last Name',
      priority: 2,
      filterDropdown: TextSearchTableFilter,
      width: 150,
      className: 'sentry-mask',
      render: (...args) => renderName(isOrganization, ...args),
    }),
    column({
      key: 'email',
      title: 'Email',
      priority: 3,
      width: 300,
      className: 'sentry-mask',
      filterDropdown: TextSearchTableFilter,
    }),
    column({
      key: 'role',
      dataIndex: 'roleName',
      title: 'Role',
      priority: 4,
      width: 180,
      filterDropdown: MultiSelectTableFilter,
    }),
    column({
      key: 'employeeId',
      dataIndex: 'employeeId',
      title: 'Employee ID',
      priority: 5,
      width: 180,
      filterDropdown: TextSearchTableFilter,
    }),
    ...(isOrganization
      ? [
          column({
            key: 'organizationBusinessesCount',
            title: 'Facilities',
            priority: 6,
          }),
          column({
            key: 'organizationUnitsCount',
            title: 'Units',
            priority: 7,
          }),
        ]
      : []),
    column({
      key: 'active',
      dataIndex: 'active',
      title: 'Status',
      priority: 10,
      width: 120,
      filterDropdown: MultiSelectTableFilter,
      render: (active: boolean) => (
        <Text weight="bold" color={active ? 'meadowDarker' : 'midnightLighter'}>
          {active ? 'Active' : 'Inactive'}
        </Text>
      ),
    }),
    ...(isOrganization
      ? [
          column({
            key: 'updatedAt',
            title: 'Last Updated',
            width: 208,
            priority: 9,
            render: (updatedAt: string) => (
              <TimeWithTimeZone date={updatedAt} variant="long_format" />
            ),
          }),
        ]
      : []),
  ];
}

export const MembersList = ({
  entityType,
}: {
  entityType: 'organization' | 'supplier';
}) => {
  const navigate = useNavigate();
  const currentMembership = useCurrentMembership();
  const { state } = useLocation() as {
    state: { updatedMemberIds?: string[] };
  };
  const updatedMemberIds = state?.updatedMemberIds || [];
  const isOrganization = entityType === 'organization';

  const initialFilters = useMemo(() => {
    const stored = FiltersStorage.getFilters(LOCALSTORAGE_FILTER_KEY);
    return Object.keys(stored).length === 0 ? { active: ['true'] } : stored;
  }, []);

  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: LOCALSTORAGE_FILTER_KEY,
    filtersOverrideDefault: initialFilters,
    defaultPaginationData: {
      limit: 10,
      offset: 0,
      defaultPageSize: 10,
    },
    columnsDictionary: {
      firstName: 'firstName',
      lastName: 'lastName',
      email: 'email',
      role: 'role',
      employeeId: 'employeeId',
      active: 'active',
      organizationBusinessesCount: 'organizationBusinessesCount',
      organizationUnitsCount: 'organizationUnitsCount',
      updatedAt: 'updatedAt',
    },
  });

  const queryVariables = useMemo(() => {
    const { offset, limit, orderField, orderDirection, defaultPageSize } =
      paginationVariables;
    const pageSize = limit ?? defaultPageSize;
    return {
      filters,
      pagination: {
        page: Math.floor(offset / pageSize) + 1,
        pageSize,
      },
      orders: orderField
        ? { [orderField]: orderDirection === 'DESC' ? 'descend' : 'ascend' }
        : {},
    };
  }, [paginationVariables, filters]);

  const { data, loading, error } = useQuery<WorksMembershipsResponse>(
    WorksMembershipsQuery,
    {
      variables: {
        input: queryVariables,
      },
    }
  );

  const membershipRoles = useQuery<WorksMembershipRolesResponse>(
    WorksMembershipRolesQuery
  );
  const memberships = data?.worksMemberships?.collection;
  const canUpdateAdminUsersUnits = currentMembership.canUpdateAdminUsersUnits;

  const showActionColumn = useMemo(
    () =>
      memberships?.some((membership) => !membership.organizationUnitsCount) &&
      isOrganization,
    [memberships, isOrganization]
  );

  const redirectToUpdateLocations = useCallback(
    (id?: string) => {
      navigate(`${ORGANIZATION_ADMIN_MENU.USERS.ROUTE}/update-locations`, {
        state: {
          memberIds: [id],
        },
      });
    },
    [navigate]
  );

  const tableColumnsWithFilters = useCallback(
    (isOrganization: boolean) => {
      const columns = getColumns(
        {
          paginated: true,
          filterConfig: {
            selectedFilters: filters,
            filterOptions: {
              role: membershipRoles?.data?.worksMembershipRoles || [],
              active: ACTIVE_FILTER_OPTIONS,
            },
          },
        },
        isOrganization
      );

      const actionColumn: TableColumnType<Membership> = {
        key: 'id',
        fixed: 'right',
        width: 152,
        render: (record: Membership) => {
          return record.organizationUnitsCount ? null : (
            <PrimaryButton
              className="members-list__action-button"
              onClick={() => redirectToUpdateLocations(record.id)}
              disabled={!record.canUserUpdateMembership}
            >
              Assign Locations
            </PrimaryButton>
          );
        },
      };

      if (showActionColumn) {
        columns.push(actionColumn);
      }

      return columns;
    },
    [
      filters,
      membershipRoles?.data?.worksMembershipRoles,
      redirectToUpdateLocations,
      showActionColumn,
    ]
  );

  if (error) {
    return <ErrorHandler error={error} log="MembersList" />;
  }

  const addNewDropdown = [
    {
      label: (
        <Link
          to={`${
            isOrganization
              ? ORGANIZATION_ADMIN_MENU.USERS.ROUTE
              : SUPPLIER_ADMIN_MENU.USERS.ROUTE
          }/add`}
        >
          <Icon icon="user" kind="light" /> Create User
        </Link>
      ),
      key: 'single',
    },
    {
      label: (
        <Link
          to={`${
            isOrganization
              ? ORGANIZATION_ADMIN_MENU.USERS.ROUTE
              : SUPPLIER_ADMIN_MENU.USERS.ROUTE
          }/upload`}
        >
          <Icon icon="arrowUpFromBracket" kind="light" /> Upload Users
        </Link>
      ),
      key: 'multiple',
    },
  ];

  return (
    <>
      <section>
        <FilteredTable
          className="members-list"
          rowKey="id"
          columns={tableColumnsWithFilters(isOrganization)}
          filters={filters}
          dataSource={memberships}
          loading={loading}
          localStorageFilterKey={localStorageFilterKey}
          onChange={tableChangeHandler}
          pagination={{
            total: data?.worksMemberships?.metadata?.totalCount,
            current:
              Math.floor(
                paginationVariables.offset / paginationVariables.limit
              ) + 1,
            pageSize: paginationVariables.limit,
          }}
          onFilterChange={filterChangeHandler}
          onRow={(record: Membership) => ({
            onClick: record.organizationUnitsCount
              ? () => navigate(buildViewDrawerRoute(record.id))
              : null,
          })}
          rowClassName={(record: {
            id: string;
            organizationUnitsCount: number;
          }) => ({
            'highlight-recently-updated': updatedMemberIds.includes(record.id),
            'highlight-action-required':
              isOrganization && !record.organizationUnitsCount,
          })}
          headerRightSlot={
            <ButtonGroup>
              {canUpdateAdminUsersUnits && (
                <Link
                  to={`${ORGANIZATION_ADMIN_MENU.USERS.ROUTE}/update-locations`}
                >
                  <AddButton>Assign Locations</AddButton>
                </Link>
              )}
              <Dropdown
                overlayClassName="admin-members-page__add-new-dropdown"
                menu={{ items: addNewDropdown }}
                placement="bottomRight"
              >
                <AddButton showDropdown>Users</AddButton>
              </Dropdown>
            </ButtonGroup>
          }
        />
      </section>
      <style jsx>{`
        :global(.members-list .ant-table-tbody > tr > td) {
          padding: 20px 4px 20px 8px;
        }
        :global(td > a.members-list-row-link) {
          cursor: pointer;
          padding: 18px 12px 18px 0;
          font-weight: 800;
        }
        :global(.members-list .members-list__action-button) {
          margin-top: -6px;
          margin-bottom: -6px;
        }
        :global(.admin-members-page__add-new-dropdown a) {
          width: 100%;
        }
      `}</style>
    </>
  );
};
